import { format as formatTZ, toZonedTime } from "date-fns-tz";
import { useMemo } from "react";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { DISCOVERED_TAG_CATEGORIES } from "@/lib/constants/discovered-tag-categories";
import { useFeature } from "@/lib/feature-management";
import type { SelectedFiltersInput } from "@/lib/utils/filters";
import { buildAndFromSelected } from "@/lib/utils/filters";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import type { TanStackRouteSearch } from "@/types/url-params.types";
import { useAnalysisParamsContext } from "./analysis-params.context";

const TIME_ZONE = "UTC";

/**
 * Feed params for queries from {@link AnalysisParamsProvider} URL state.
 * Outside the provider, returns a stable empty shape so urql hooks stay paused (`!feedId`, etc.).
 */
export const useAnalysisParamsForQueries = (): {
  feedId: string | undefined;
  startDate: string | undefined;
  endDate: string | undefined;
  /** Original date range from URL, unchanged by chart bar selection. */
  originalStartDate: string | undefined;
  /** Original date range from URL, unchanged by chart bar selection. */
  originalEndDate: string | undefined;
  selectedFilters: SelectedFiltersInput | undefined;
  period: TanStackRouteSearch["period"] | undefined;
  filterDays: number | undefined;
  isCustomDaysRange: boolean;
  feedVolumeStartDate: string | null;
  feedVolumeEndDate: string | null;
  chartSelection: string | null;
} => {
  const analysis = useAnalysisParamsContext();
  const discoveredTagsHidden = useFeature("discovered-tags-hidden", false);

  const activeBar = useFeedContentManagementStore((state) => state.activeBar);
  const chartSelection = useFeedContentManagementStore(
    (state) => state.chartSelection,
  );
  const selectedKeyMessageFilterExclusion = useKeyMessageStore(
    (state) => state.selectedKeyMessageFilterExclusion,
  );

  const feedVolumeStartDate = useMemo(() => {
    const startOfPeriod =
      activeBar && "startOfPeriod" in activeBar
        ? (activeBar.startOfPeriod as string | undefined)
        : undefined;
    return startOfPeriod
      ? formatTZ(
          toZonedTime(new Date(startOfPeriod), TIME_ZONE),
          "yyyy-MM-dd",
          {
            timeZone: TIME_ZONE,
          },
        )
      : null;
  }, [activeBar]);

  const feedVolumeEndDate = useMemo(() => {
    const endOfPeriod =
      activeBar && "endOfPeriod" in activeBar
        ? (activeBar.endOfPeriod as string | undefined)
        : undefined;
    return endOfPeriod
      ? formatTZ(toZonedTime(new Date(endOfPeriod), TIME_ZONE), "yyyy-MM-dd", {
          timeZone: TIME_ZONE,
        })
      : null;
  }, [activeBar]);

  return useMemo(() => {
    if (analysis) {
      const keyMessageConjunction =
        analysis.keyMessageConjunction ?? selectedKeyMessageFilterExclusion;
      const andCategories =
        keyMessageConjunction === "AND" ? new Set(["KEY_MESSAGE"]) : undefined;
      const effectiveSelectedFilters = discoveredTagsHidden
        ? Object.fromEntries(
            Object.entries(analysis.selectedFilters).filter(
              ([category]) =>
                !DISCOVERED_TAG_CATEGORIES.has(
                  category as ShareVoiceSidebarCategory,
                ),
            ),
          )
        : analysis.selectedFilters;
      const selectedFilters = buildAndFromSelected(
        effectiveSelectedFilters,
        andCategories,
      );
      const effectiveStartDate =
        feedVolumeStartDate && feedVolumeEndDate
          ? feedVolumeStartDate
          : analysis.startDate;
      const effectiveEndDate =
        feedVolumeStartDate && feedVolumeEndDate
          ? feedVolumeEndDate
          : analysis.endDate;
      return {
        feedId: analysis.feedId,
        startDate: effectiveStartDate,
        endDate: effectiveEndDate,
        originalStartDate: analysis.startDate,
        originalEndDate: analysis.endDate,
        selectedFilters,
        period: analysis.period,
        filterDays: analysis.filterDays,
        isCustomDaysRange: analysis.isCustomDaysRange,
        feedVolumeStartDate,
        feedVolumeEndDate,
        chartSelection,
      };
    }
    return {
      feedId: undefined,
      startDate: undefined,
      endDate: undefined,
      originalStartDate: undefined,
      originalEndDate: undefined,
      selectedFilters: undefined,
      period: undefined,
      filterDays: undefined,
      isCustomDaysRange: false,
      feedVolumeStartDate: null,
      feedVolumeEndDate: null,
      chartSelection: null,
    };
  }, [
    analysis,
    discoveredTagsHidden,
    selectedKeyMessageFilterExclusion,
    feedVolumeStartDate,
    feedVolumeEndDate,
    chartSelection,
  ]);
};
